import React, { useState, useRef, useContext } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip,
  Grid,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  CloudUpload as UploadIcon,
  FolderOpen as FolderIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Celebration as CelebrationIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 500,
  },
  stepper: {
    backgroundColor: "transparent",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
  stepCompleted: {
    "& .MuiStepIcon-root.MuiStepIcon-completed": {
      color: "#4caf50",
    },
  },
  contentContainer: {
    minHeight: 400,
    padding: theme.spacing(2),
  },
  uploadArea: {
    border: "2px dashed #2196f3",
    borderRadius: 16,
    padding: theme.spacing(6),
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "#f5faff",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#e3f2fd",
      borderColor: "#1976d2",
    },
  },
  uploadIcon: {
    fontSize: 64,
    color: "#ffc107",
    marginBottom: theme.spacing(2),
  },
  previewTable: {
    maxHeight: 300,
    overflow: "auto",
  },
  columnSelect: {
    minWidth: 180,
    "& .MuiSelect-select": {
      padding: "8px 14px",
    },
  },
  columnHeader: {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    fontSize: "0.75rem",
    textTransform: "uppercase",
    color: "#666",
  },
  mappedField: {
    backgroundColor: "#e8f5e9",
    "& .MuiSelect-root": {
      color: "#2e7d32",
    },
  },
  requiredField: {
    backgroundColor: "#fff3e0",
    "& .MuiSelect-root": {
      color: "#e65100",
    },
  },
  ignoredField: {
    backgroundColor: "#f5f5f5",
    opacity: 0.7,
  },
  optionsSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#fafafa",
    borderRadius: 8,
  },
  resultCard: {
    textAlign: "center",
    padding: theme.spacing(4),
    backgroundColor: "#e8f5e9",
    borderRadius: 16,
  },
  resultIcon: {
    fontSize: 80,
    color: "#4caf50",
    marginBottom: theme.spacing(2),
  },
  statsBox: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(4),
    marginTop: theme.spacing(3),
  },
  statItem: {
    textAlign: "center",
    padding: theme.spacing(2),
    backgroundColor: "white",
    borderRadius: 12,
    minWidth: 120,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  statNumber: {
    fontSize: "2.5rem",
    fontWeight: "bold",
  },
  statLabel: {
    fontSize: "0.875rem",
    color: "#666",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
  legendBox: {
    width: 16,
    height: 16,
    borderRadius: 4,
  },
  infoBox: {
    backgroundColor: "#e3f2fd",
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
  },
}));

const FIELD_OPTIONS = [
  { value: "", label: "Ignorar coluna" },
  { value: "name", label: "📛 Nome do Contato" },
  { value: "number", label: "📱 Número de Telefone (obrigatório)", required: true },
  { value: "var1", label: "📝 Variável 1 (personalização)" },
  { value: "var2", label: "📝 Variável 2 (personalização)" },
  { value: "var3", label: "📝 Variável 3 (personalização)" },
  { value: "var4", label: "📝 Variável 4 (personalização)" },
  { value: "var5", label: "📝 Variável 5 (personalização)" },
  { value: "var6", label: "📝 Variável 6 (personalização)" },
  { value: "tags", label: "🏷️ Tags" },
  { value: "optedIn", label: "✅ Opted-in" },
];

const steps = ["Upload", "Mapear Colunas", "Resultado"];

const ContactListProImportWizard = ({ open, onClose, onSuccess, existingListId, existingListName }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [listName, setListName] = useState("");
  const [allowDuplicates, setAllowDuplicates] = useState(true);
  const [optIn, setOptIn] = useState(true);
  const [confirmAuth, setConfirmAuth] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const extension = selectedFile.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "csv", "txt"].includes(extension)) {
      toast.error("Formato de arquivo não suportado. Use Excel (.xlsx), CSV ou TXT.");
      return;
    }

    setFile(selectedFile);
    setListName(selectedFile.name.replace(/\.[^/.]+$/, ""));
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const { data } = await api.post("/contact-lists-pro/upload-file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setFileData(data);

      // Auto-mapear colunas com nomes comuns
      const autoMapping = {};
      data.columns.forEach((col) => {
        const colLower = col.toLowerCase();
        if (colLower.includes("nome") || colLower.includes("name")) {
          autoMapping[col] = "name";
        } else if (
          colLower.includes("telefone") ||
          colLower.includes("phone") ||
          colLower.includes("numero") ||
          colLower.includes("celular") ||
          colLower.includes("whatsapp")
        ) {
          autoMapping[col] = "number";
        }
      });
      setColumnMapping(autoMapping);

      setActiveStep(1);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleColumnMapChange = (column, value) => {
    const newMapping = { ...columnMapping };

    // Se já existe outra coluna com este valor, limpar ela
    if (value) {
      Object.keys(newMapping).forEach((key) => {
        if (newMapping[key] === value) {
          delete newMapping[key];
        }
      });
    }

    if (value) {
      newMapping[column] = value;
    } else {
      delete newMapping[column];
    }

    setColumnMapping(newMapping);
  };

  const getMappedFieldForColumn = (column) => {
    return columnMapping[column] || "";
  };

  const isNumberMapped = () => {
    return Object.values(columnMapping).includes("number");
  };

  const getReversedMapping = () => {
    const reversed = {};
    Object.entries(columnMapping).forEach(([column, field]) => {
      reversed[field] = column;
    });
    return reversed;
  };

  const handleImport = async () => {
    if (!isNumberMapped()) {
      toast.error("O campo Número de Telefone é obrigatório!");
      return;
    }

    if (!confirmAuth) {
      toast.error("Você precisa confirmar a autorização para contatar estas pessoas.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", existingListId ? existingListName || listName : listName);
      formData.append("columnMapping", JSON.stringify(getReversedMapping()));
      formData.append("allowDuplicates", allowDuplicates.toString());
      formData.append("optIn", optIn.toString());
      if (existingListId) {
        formData.append("contactListProId", existingListId.toString());
      }

      const { data } = await api.post("/contact-lists-pro/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setImportResult(data);
      setActiveStep(2);

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFile(null);
    setFileData(null);
    setColumnMapping({});
    setListName("");
    setAllowDuplicates(true);
    setOptIn(true);
    setConfirmAuth(false);
    setImportResult(null);
    onClose();
  };

  const getSelectStyle = (column) => {
    const value = getMappedFieldForColumn(column);
    if (value === "number") return classes.requiredField;
    if (value) return classes.mappedField;
    return classes.ignoredField;
  };

  const renderUploadStep = () => (
    <Box className={classes.contentContainer}>
      <Typography variant="h6" gutterBottom>
        <FolderIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
        Selecione seu Arquivo
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Faça upload de uma planilha CSV. O sistema detectará automaticamente as colunas.
      </Typography>

      <Box
        className={classes.uploadArea}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".xlsx,.xls,.csv,.txt"
          style={{ display: "none" }}
        />
        <FolderIcon className={classes.uploadIcon} />
        <Typography variant="h6">Clique aqui ou arraste seu arquivo</Typography>
        <Typography variant="body2" color="textSecondary">
          Excel (.xlsx), CSV ou TXT
        </Typography>
      </Box>

      <Box className={classes.infoBox}>
        <Typography variant="subtitle2" style={{ color: "#1565c0" }}>
          💡 Como exportar do Excel:
        </Typography>
        <Typography variant="body2" component="ol" style={{ marginTop: 8, paddingLeft: 20 }}>
          <li>Abra sua planilha no Excel</li>
          <li>Vá em <strong>Arquivo → Salvar como</strong></li>
          <li>Em "Tipo", escolha <strong>"CSV (separado por ponto-e-vírgula)"</strong></li>
          <li>Clique em Salvar</li>
        </Typography>
      </Box>
    </Box>
  );

  const renderMappingStep = () => (
    <Box className={classes.contentContainer}>
      <Typography variant="h6" gutterBottom>
        🔗 Mapear Colunas
      </Typography>
      <Typography variant="body2" color="textSecondary">
        <strong>Arquivo:</strong> {file?.name} | <strong>Total:</strong> {fileData?.totalRows?.toLocaleString()} contatos
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Vincule cada coluna do seu arquivo ao campo correspondente. O <span style={{ color: "#e65100" }}>Número de Telefone</span> é obrigatório.
      </Typography>

      <Box display="flex" gap={2} mb={2}>
        <Box className={classes.legendItem}>
          <Box className={classes.legendBox} style={{ backgroundColor: "#fff3e0" }} />
          <Typography variant="caption">Campo obrigatório (Telefone)</Typography>
        </Box>
        <Box className={classes.legendItem}>
          <Box className={classes.legendBox} style={{ backgroundColor: "#e8f5e9" }} />
          <Typography variant="caption">Campo mapeado</Typography>
        </Box>
        <Box className={classes.legendItem}>
          <Box className={classes.legendBox} style={{ backgroundColor: "#f5f5f5" }} />
          <Typography variant="caption">Ignorado</Typography>
        </Box>
      </Box>

      <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
        📋 Preview dos Dados
      </Typography>

      <TableContainer component={Paper} className={classes.previewTable}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {fileData?.columns.map((col, idx) => (
                <TableCell key={idx} className={classes.columnHeader}>
                  <Typography variant="caption" display="block" style={{ marginBottom: 4 }}>
                    COLUNA {idx + 1}
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: "bold" }}>
                    {col}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {fileData?.columns.map((col, idx) => (
                <TableCell key={idx} className={getSelectStyle(col)}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={getMappedFieldForColumn(col)}
                      onChange={(e) => handleColumnMapChange(col, e.target.value)}
                      className={classes.columnSelect}
                      displayEmpty
                    >
                      {FIELD_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={
                            option.value &&
                            Object.values(columnMapping).includes(option.value) &&
                            columnMapping[col] !== option.value
                          }
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {fileData?.preview.map((row, rowIdx) => (
              <TableRow key={rowIdx}>
                {fileData?.columns.map((col, colIdx) => (
                  <TableCell key={colIdx}>
                    <Typography variant="body2" noWrap style={{ maxWidth: 150 }}>
                      {row[col] || <em style={{ color: "#999" }}>(vazio)</em>}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
        Mostrando 10 de {fileData?.totalRows?.toLocaleString()} linhas
      </Typography>

      <Box className={classes.optionsSection}>
        <Typography variant="subtitle2" gutterBottom>
          ⚙️ Opções
        </Typography>
        {existingListId ? (
          <Typography variant="body2" color="primary" style={{ marginBottom: 8 }}>
            📋 Adicionando contatos à lista: <strong>{existingListName || "Lista existente"}</strong>
          </Typography>
        ) : (
          <TextField
            label="Nome da Lista"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            fullWidth
            margin="dense"
            variant="outlined"
            size="small"
            helperText="Este nome será usado para identificar a lista nas campanhas"
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
              color="primary"
            />
          }
          label="Marcar todos como autorizados (opt-in) - Todos os contatos poderão receber campanhas automaticamente"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={allowDuplicates}
              onChange={(e) => setAllowDuplicates(e.target.checked)}
              color="primary"
            />
          }
          label="Permitir números duplicados - Importa o mesmo número várias vezes (útil para enviar múltiplas mensagens com variáveis diferentes)"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmAuth}
              onChange={(e) => setConfirmAuth(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" style={{ fontWeight: "bold" }}>
              ✅ Confirmo que tenho autorização para contatar estas pessoas
            </Typography>
          }
        />
      </Box>
    </Box>
  );

  const renderResultStep = () => (
    <Box className={classes.contentContainer}>
      <Box className={classes.resultCard}>
        <Typography variant="h1" style={{ fontSize: 64, marginBottom: 16 }}>
          🎉
        </Typography>
        <Typography variant="h4" style={{ color: "#2e7d32", fontWeight: "bold" }}>
          Importação Concluída!
        </Typography>

        <Box className={classes.statsBox}>
          <Box className={classes.statItem}>
            <Typography className={classes.statNumber} style={{ color: "#2e7d32" }}>
              {importResult?.imported?.toLocaleString()}
            </Typography>
            <Typography className={classes.statLabel}>Importados</Typography>
          </Box>
          <Box className={classes.statItem}>
            <Typography className={classes.statNumber} style={{ color: "#ff9800" }}>
              {importResult?.duplicates?.toLocaleString()}
            </Typography>
            <Typography className={classes.statLabel}>Duplicados (importados)</Typography>
          </Box>
          <Box className={classes.statItem}>
            <Typography className={classes.statNumber} style={{ color: "#f44336" }}>
              {importResult?.invalid?.toLocaleString()}
            </Typography>
            <Typography className={classes.statLabel}>Ignorados (inválidos)</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            📥 Importar Contatos
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {steps.map((label, idx) => (
            <Step key={label} className={idx < activeStep ? classes.stepCompleted : ""}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>

      <DialogContent dividers className={classes.root}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        )}
        {!loading && activeStep === 0 && renderUploadStep()}
        {!loading && activeStep === 1 && renderMappingStep()}
        {!loading && activeStep === 2 && renderResultStep()}
      </DialogContent>

      <DialogActions>
        {activeStep === 1 && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setActiveStep(0);
              setFile(null);
              setFileData(null);
              setColumnMapping({});
            }}
          >
            ← Escolher Outro Arquivo
          </Button>
        )}
        <Box flex={1} />
        {activeStep === 1 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckIcon />}
            onClick={handleImport}
            disabled={!isNumberMapped() || !confirmAuth || loading}
          >
            ✅ Importar {fileData?.totalRows?.toLocaleString()} Contatos
          </Button>
        )}
        {activeStep === 2 && (
          <>
            <Button onClick={handleClose} variant="outlined" color="primary">
              📋 Ver Meus Contatos
            </Button>
            <Button
              onClick={() => {
                setActiveStep(0);
                setFile(null);
                setFileData(null);
                setColumnMapping({});
                setImportResult(null);
              }}
              variant="contained"
              color="primary"
            >
              📤 Importar Mais
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactListProImportWizard;

